import { DateFormat } from '@const/consts';
import {
  EnvelopeFieldType,
  IEnvelopeField,
  IEnvelopeSelectColumn,
  isNumericEnvelopeFieldType,
} from '@store/modules/boxes/types';
import {
  formatDecimalValue,
  formatSelectDisplayTemplate,
  getDateFormat,
  getLocaleDateFormat,
} from '@utils/utils';

const selectDisplayPriorityKeys = ['name', 'title', 'label', 'value'];

const getStringValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  return `${value}`;
};

const getBooleanDisplayValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return ['true', '1', 'yes', 'y'].includes(value.trim().toLowerCase());

  return Boolean(value);
};

const getSelectColumnDisplayValue = (
  fields: Record<string, unknown>,
  column: IEnvelopeSelectColumn
): string => getStringValue(fields[column.systemKey]);

const getSelectFieldsFallbackValue = (
  field: IEnvelopeField,
  fields: Record<string, unknown> | null | undefined
): string => {
  if (!fields) return '';

  const visibleValues = (field.selectColumns ?? [])
    .filter(({ isVisibleAfterSelection }) => isVisibleAfterSelection)
    .map((column) => getSelectColumnDisplayValue(fields, column))
    .filter(Boolean);

  if (visibleValues.length) return visibleValues.join(', ');

  const priorityValue = selectDisplayPriorityKeys
    .map((key) => getStringValue(fields[key]))
    .find(Boolean);

  if (priorityValue) return priorityValue;

  const firstTextValue = Object.values(fields)
    .map(getStringValue)
    .find(Boolean);

  return firstTextValue ?? '';
};

const getSelectTemplateDisplayValue = (
  field: IEnvelopeField,
  fields: Record<string, unknown> | null | undefined
): string => (
  field.displayTemplate && fields
    ? formatSelectDisplayTemplate(field.displayTemplate, fields)
    : ''
);

export const getEnvelopeFieldDisplayValue = (
  field: IEnvelopeField | null,
  value: unknown,
  language: string,
  t: (key: string) => unknown
): string => {
  const getSelectValue = (): string => {
    if (typeof value === 'string') return value;

    if (field && value && typeof value === 'object' && !Array.isArray(value)) {
      const {
        displayTemplate,
        key,
        fields: resolvedFields,
      } = value as {
        displayTemplate?: unknown;
        key?: unknown;
        fields?: Record<string, unknown> | null;
      };

      const templateValue = getSelectTemplateDisplayValue(field, resolvedFields);

      if (templateValue) return templateValue;

      const fallbackValue = getSelectFieldsFallbackValue(field, resolvedFields);

      if (typeof displayTemplate === 'string' && displayTemplate) return displayTemplate;

      if (typeof key !== 'string') {
        if (!field.displayTemplate || !resolvedFields) return fallbackValue;

        return fallbackValue;
      }

      if (!field.displayTemplate || !resolvedFields) return fallbackValue || key;

      return fallbackValue || key;
    }

    return '';
  };

  if (value === null || value === undefined || Array.isArray(value)) return '';

  if (!field) {
    if (typeof value === 'object') {
      if (typeof (value as { displayTemplate?: unknown }).displayTemplate === 'string') {
        return (value as { displayTemplate: string }).displayTemplate;
      }

      const fields = (value as { fields?: unknown }).fields;

      if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
        const priorityValue = selectDisplayPriorityKeys
          .map((key) => getStringValue((fields as Record<string, unknown>)[key]))
          .find(Boolean);

        if (priorityValue) return priorityValue;
      }

      if (typeof (value as { key?: unknown }).key === 'string') {
        return (value as { key: string }).key;
      }

      return JSON.stringify(value);
    }

    return `${value}`;
  }

  if (field.type === EnvelopeFieldType.SELECT) {
    return getSelectValue();
  }

  if (field.type === EnvelopeFieldType.BOOL) {
    return String(getBooleanDisplayValue(value) ? t('common:yes') : t('common:no'));
  }

  if (isNumericEnvelopeFieldType(field.type) && (typeof value === 'number' || typeof value === 'string')) {
    const formattedValue = formatDecimalValue(value, language, field.decimalPlaces);

    return field.type === EnvelopeFieldType.CURRENCY && field.currency
      ? `${formattedValue} ${field.currency}`
      : formattedValue;
  }

  if (typeof value === 'string') {
    if (field.type === EnvelopeFieldType.DATE) {
      return getLocaleDateFormat(value, language);
    }

    if (field.type === EnvelopeFieldType.DATETIME) {
      return `${getLocaleDateFormat(value, language)} ${getDateFormat(
        value,
        DateFormat.TIME_ONLY
      )}`;
    }
  }

  return `${value}`;
};
